import { useState, useRef, useEffect } from 'react';
import { ChevronDown, LogOut, Settings } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { useAuth } from '../../lib/auth.js';
import { RoleBadge } from '../ui/Badge.js';
import { useToast } from '../ui/Toast.js';
import { cn } from '../../lib/cn.js';

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

export function UserMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!user) return null;

  function handleLogout() {
    setOpen(false);
    logout();
    navigate({ to: '/login' });
  }

  function handleSettings() {
    setOpen(false);
    toast('Settings coming soon', 'info');
  }

  const initials = getInitials(user.name);

  const roleLabel =
    user.role === 'super_admin' ? 'Super Admin'
    : user.role === 'admin' ? 'Admin'
    : user.role === 'manager' ? 'Manager'
    : 'Agent';

  return (
    <div ref={containerRef} className="relative">
      <button
        className={cn(
          'flex items-center gap-2 py-1.5 px-2 rounded-xl border border-transparent',
          'hover:bg-hover-bg hover:border-border transition-colors min-h-[44px]',
          open && 'bg-hover-bg border-border'
        )}
        onClick={() => setOpen((v) => !v)}
        aria-label="User menu"
        aria-expanded={open}
        aria-haspopup="true"
      >
        {/* Avatar circle with initials */}
        <div
          className="w-7 h-7 rounded-full bg-primary flex items-center justify-center text-white font-semibold text-xs flex-shrink-0"
          aria-hidden="true"
        >
          {initials}
        </div>
        {/* Name + role (desktop only) */}
        <div className="hidden sm:flex flex-col items-start min-w-0">
          <span className="text-xs font-semibold text-primary-text leading-tight truncate max-w-[100px]">
            {user.name}
          </span>
          <span className="text-[10px] text-secondary leading-tight">{roleLabel}</span>
        </div>
        <ChevronDown
          className={cn(
            'hidden sm:block w-3.5 h-3.5 text-secondary transition-transform',
            open && 'rotate-180'
          )}
        />
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-border rounded-xl shadow-lg w-56 py-1">
            {/* User info header */}
            <div className="px-3 py-3 border-b border-border">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                  {initials}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-primary-text truncate">{user.name}</p>
                  <p className="text-xs text-secondary truncate">{user.email}</p>
                </div>
              </div>
              <div className="mt-2">
                <RoleBadge role={user.role} />
              </div>
            </div>

            <button
              onClick={handleSettings}
              className="flex items-center gap-3 px-3 py-2.5 text-sm text-primary-text hover:bg-hover-bg w-full text-left"
            >
              <Settings className="w-4 h-4 text-secondary" />
              Settings
            </button>

            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-3 py-2.5 text-sm text-danger hover:bg-red-50 w-full text-left"
            >
              <LogOut className="w-4 h-4" />
              Sign out
            </button>
          </div>
        </>
      )}
    </div>
  );
}
